import React from 'react';
import { MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

interface LocationPageProps {
  city: string;
  region: string;
  description: string;
  serviceAreas: string[];
  highlights: string[];
}

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const LocationPage: React.FC<LocationPageProps> = ({ city, region, description, serviceAreas, highlights }) => {
  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)',
          borderRadius: '24px',
          marginBottom: '64px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          padding: '80px 24px',
          marginTop: '60px',
        }}
      >
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(245,158,11,0.06)', filter: 'blur(60px)' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(139,92,246,0.06)', filter: 'blur(40px)' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px', margin: '0 auto' }}>
          <AnimatedSection>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
              <MapPin size={20} style={{ color: '#F59E0B' }} />
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Serving {region}
              </span>
            </div>
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 800, fontFamily: 'var(--font-heading)', marginBottom: '16px', lineHeight: 1.2, color: '#fff' }}>
              AI & Cloud Solutions in <span style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{city}</span>
            </h1>
            <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, marginBottom: '32px' }}>
              {description}
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link
                to="/contact"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 28px',
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: '#0F172A',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Schedule Consultation <ArrowRight size={16} />
              </Link>
              <Link
                to="/services"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 28px',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textDecoration: 'none',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
              >
                View Services
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Service Areas */}
      <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', marginBottom: '80px' }}>
        <AnimatedSection>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, fontFamily: 'var(--font-heading)', marginBottom: '16px', textAlign: 'center' }}>
            Serving the Greater {region} Area
          </h2>
          <p style={{ fontSize: '1rem', color: 'var(--color-gray-500)', textAlign: 'center', marginBottom: '40px', maxWidth: '600px', margin: '0 auto' }}>
            We proudly serve businesses throughout the DFW metroplex
          </p>
        </AnimatedSection>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '40px' }}>
          {serviceAreas.map((area, idx) => (
            <AnimatedSection key={area} delay={idx * 100}>
              <div
                style={{
                  padding: '24px',
                  background: 'rgba(245,158,11,0.05)',
                  border: '1px solid rgba(245,158,11,0.2)',
                  borderRadius: '12px',
                  textAlign: 'center',
                }}
              >
                <MapPin size={24} style={{ color: '#F59E0B', margin: '0 auto 12px' }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px' }}>{area}</h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--color-gray-500)' }}>
                  AI & Cloud Engineering Services
                </p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Highlights */}
      <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', marginBottom: '80px' }}>
        <AnimatedSection>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, fontFamily: 'var(--font-heading)', marginBottom: '48px', textAlign: 'center' }}>
            Why Choose FretDev for {city}?
          </h2>
        </AnimatedSection>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
          {highlights.map((highlight, idx) => (
            <AnimatedSection key={highlight} delay={idx * 100}>
              <div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <CheckCircle size={24} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '4px' }} />
                  <div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px' }}>
                      {highlight.split(':')[0]}
                    </h3>
                    <p style={{ fontSize: '0.95rem', color: 'var(--color-gray-500)', lineHeight: 1.6 }}>
                      {highlight.split(':')[1]}
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        style={{
          background: 'linear-gradient(135deg, #1E3A5F 0%, #0F172A 100%)',
          borderRadius: '24px',
          padding: '64px 24px',
          textAlign: 'center',
          marginBottom: '80px',
          maxWidth: '1200px',
          margin: '0 auto 80px',
        }}
      >
        <AnimatedSection>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, fontFamily: 'var(--font-heading)', marginBottom: '16px', color: '#fff' }}>
            Ready to Transform Your Business?
          </h2>
          <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.7)', marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
            Schedule a free consultation to discuss how AI can drive measurable results for your {city} business.
          </p>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: '#0F172A',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 700,
              textDecoration: 'none',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Get Started <ArrowRight size={18} />
          </Link>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default LocationPage;
